import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Checkbox,
  FormControlLabel,
  TextField,
  Grid,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import api from '../services/api';
import { Invoice } from '../types';
import { format } from 'date-fns';

const ContractorInvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [certifying, setCertifying] = useState(false);
  const [certified, setCertified] = useState(false);
  const [certificationNotes, setCertificationNotes] = useState('');
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);

  const fetchInvoice = useCallback(async () => {
    if (!id) {
      return;
    }
    try {
      setLoading(true);
      const response = await api.get(`/contractor/invoices/${id}`);
      setInvoice(response.data);
      setCertified(response.data.status === 'CERTIFIED' || response.data.status === 'PAID');
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handleCertify = async () => {
    if (!certified) {
      alert('Please confirm that the hours are accurate');
      return;
    }

    setCertifying(true);
    try {
      await api.post(`/contractor/invoices/${id}/certify`, {
        certificationNotes,
      });
      await fetchInvoice();
      alert('Invoice certified successfully!');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to certify invoice');
    } finally {
      setCertifying(false);
    }
  };

  const handleUploadPdf = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('pdfBill', file);

      await api.post(`/contractor/invoices/${id}/upload-pdf`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('PDF bill uploaded successfully');
      await fetchInvoice();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to upload PDF bill');
    } finally {
      setUploadingPdf(false);
      // Reset input
      event.target.value = '';
    }
  };

  const handleUploadExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];

    if (!allowedTypes.includes(file.type)) {
      alert('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploadingExcel(true);
    try {
      const formData = new FormData();
      formData.append('excelHours', file);

      await api.post(`/contractor/invoices/${id}/upload-excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('Excel hours table uploaded successfully');
      await fetchInvoice();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to upload Excel file');
    } finally {
      setUploadingExcel(false);
      // Reset input
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!invoice) {
    return <Typography>Invoice not found</Typography>;
  }

  const canCertify = invoice.status === 'SUBMITTED' && !invoice.certifiedAt;
  const isCertified = invoice.status === 'CERTIFIED' || invoice.status === 'PAID';

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/contractor/invoices')} sx={{ mb: 2 }}>
        Back to Invoices
      </Button>

      <Typography variant="h4" gutterBottom>
        Invoice {invoice.invoiceNumber}
      </Typography>

      {invoice.status === 'DRAFT' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This is a draft invoice awaiting admin approval. Once approved, it will become final.
        </Alert>
      )}

      {invoice.status === 'REJECTED' && invoice.rejectionReason && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Invoice Rejected
          </Typography>
          <Typography variant="body2">
            <strong>Reason:</strong> {invoice.rejectionReason}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please review the rejection reason, fix any issues with your time entries, and generate a new invoice.
          </Typography>
        </Alert>
      )}

      {canCertify && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This invoice is waiting for your certification. Please review all time entries and certify if accurate.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Invoice Details</Typography>
            <Box sx={{ mt: 2 }}>
              <Typography><strong>Invoice Number:</strong> {invoice.invoiceNumber}</Typography>
              <Typography><strong>Billing Period:</strong> {format(new Date(invoice.startDate), 'MMM dd, yyyy')} - {format(new Date(invoice.endDate), 'MMM dd, yyyy')}</Typography>
              <Typography><strong>Status:</strong> <Chip label={invoice.status} size="small" /></Typography>
              {invoice.submissionDate && (
                <Typography><strong>Submitted:</strong> {format(new Date(invoice.submissionDate), 'MMM dd, yyyy')}</Typography>
              )}
              {invoice.certifiedAt && (
                <Typography><strong>Certified:</strong> {format(new Date(invoice.certifiedAt), 'MMM dd, yyyy')}</Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Financial Summary</Typography>
            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Subtotal (Before Tax Deduction):</Typography>
                <Typography>${invoice.subtotal.toFixed(2)}</Typography>
              </Box>
              {invoice.tax && invoice.tax !== 0 && (
                <>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography color="text.secondary">
                      {invoice.tax < 0 ? 'Tax Deduction' : 'Tax'}:
                    </Typography>
                    <Typography color={invoice.tax < 0 ? 'error.main' : 'text.primary'}>
                      {invoice.tax < 0 ? '-' : ''}${Math.abs(invoice.tax).toFixed(2)}
                    </Typography>
                  </Box>
                  {invoice.tax < 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      (Tax deduction percentage applied from contractor profile)
                    </Typography>
                  )}
                </>
              )}
              <Box display="flex" justifyContent="space-between" mb={1} sx={{ borderTop: '2px solid #eee', pt: 1, mt: 1 }}>
                <Typography variant="h6">Total Amount to be Paid:</Typography>
                <Typography variant="h6" color="primary.main">${invoice.total.toFixed(2)}</Typography>
              </Box>
              {invoice.payments && invoice.payments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Payments Received:</Typography>
                  {invoice.payments.map((payment) => (
                    <Box key={payment.id} display="flex" justifyContent="space-between">
                      <Typography variant="body2">
                        {format(new Date(payment.paymentDate), 'MMM dd, yyyy')}
                      </Typography>
                      <Typography variant="body2">${payment.amount.toFixed(2)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>File Attachments</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  PDF Bill
                </Typography>
                {invoice.hasPdfBill ? (
                  <Alert severity="success" sx={{ mb: 1 }}>
                    PDF bill uploaded: {invoice.pdfBillFileName}
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    No PDF bill uploaded yet
                  </Alert>
                )}
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={uploadingPdf}
                  sx={{ mt: 1 }}
                >
                  {uploadingPdf ? 'Uploading...' : invoice.hasPdfBill ? 'Replace PDF Bill' : 'Upload PDF Bill'}
                  <input
                    type="file"
                    hidden
                    accept="application/pdf"
                    onChange={handleUploadPdf}
                  />
                </Button>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Excel Hours Table
                </Typography>
                {invoice.hasExcelHours ? (
                  <Alert severity="success" sx={{ mb: 1 }}>
                    Excel hours table uploaded: {invoice.excelHoursFileName}
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    No Excel hours table uploaded yet
                  </Alert>
                )}
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={uploadingExcel}
                  sx={{ mt: 1 }}
                >
                  {uploadingExcel ? 'Uploading...' : invoice.hasExcelHours ? 'Replace Excel Hours' : 'Upload Excel Hours'}
                  <input
                    type="file"
                    hidden
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    onChange={handleUploadExcel}
                  />
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Time Entries</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell align="right">Hours</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.timeEntries?.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{format(new Date(entry.dateWorked), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>{entry.project?.name || '-'}</TableCell>
                      <TableCell align="right">{entry.hoursWorked.toFixed(2)}</TableCell>
                      <TableCell>{entry.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {invoice.timeEntries && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Total Hours:</strong> {invoice.timeEntries.reduce((sum, e) => sum + e.hoursWorked, 0).toFixed(2)}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {canCertify && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Certify Invoice</Typography>
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={certified}
                      onChange={(e) => setCertified(e.target.checked)}
                    />
                  }
                  label="I certify that the hours listed above are accurate and complete"
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Certification Notes (Optional)"
                  value={certificationNotes}
                  onChange={(e) => setCertificationNotes(e.target.value)}
                  sx={{ mt: 2 }}
                  placeholder="Any additional notes or comments..."
                />
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleCertify}
                    disabled={!certified || certifying}
                  >
                    {certifying ? 'Certifying...' : 'Certify Invoice'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        )}

        {isCertified && invoice.certificationNotes && (
          <Grid item xs={12}>
            <Alert severity="success">
              Invoice certified on {format(new Date(invoice.certifiedAt!), 'MMM dd, yyyy')}.
              {invoice.certificationNotes && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Notes:</strong> {invoice.certificationNotes}
                </Typography>
              )}
            </Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ContractorInvoiceDetail;

